import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import { getFolderChildren } from "../api/folders.api";
import { CLIENT_UPLOAD_CONCURRENCY_MAX } from "../constants/limits";
import type {
  FolderChildrenResponse,
  FolderTreeNode,
} from "../types/project-storage.types";
import { collectAllFolderIds } from "../utils/folder-tree";
import { runConcurrent } from "../utils/runConcurrent";
import { folderChildrenQueryKey } from "./useFolderChildren";

function getFolderItemCount(data: FolderChildrenResponse): number {
  return data.subfolders.length + data.files.length;
}

export function useFolderCounts(
  mmCode: number | null,
  tree: FolderTreeNode[],
  activeChildrenData?: FolderChildrenResponse
) {
  const queryClient = useQueryClient();
  const [folderCounts, setFolderCounts] = useState<Record<string, number>>({});
  const folderIds = useMemo(() => collectAllFolderIds(tree), [tree]);
  const folderIdsKey = folderIds.join("|");

  const syncCountFromCache = useCallback(() => {
    if (mmCode === null || folderIds.length === 0) return;

    const cachedCounts: Record<string, number> = {};

    folderIds.forEach((folderId) => {
      const cached = queryClient.getQueryData<FolderChildrenResponse>(
        folderChildrenQueryKey(mmCode, folderId)
      );

      if (cached) {
        cachedCounts[folderId] = getFolderItemCount(cached);
      }
    });

    if (Object.keys(cachedCounts).length === 0) return;

    setFolderCounts((current) => ({ ...current, ...cachedCounts }));
  }, [folderIds, mmCode, queryClient]);

  useEffect(() => {
    if (mmCode === null || folderIds.length === 0) return;

    let cancelled = false;

    const loadCounts = async () => {
      await runConcurrent(
        folderIds,
        CLIENT_UPLOAD_CONCURRENCY_MAX,
        async (folderId) => {
          const cacheKey = folderChildrenQueryKey(mmCode, folderId);
          const cached = queryClient.getQueryData<FolderChildrenResponse>(cacheKey);

          if (cached) {
            if (!cancelled) {
              setFolderCounts((current) => ({
                ...current,
                [folderId]: getFolderItemCount(cached),
              }));
            }
            return;
          }

          try {
            const data = await getFolderChildren(mmCode, folderId);
            queryClient.setQueryData(cacheKey, data);

            if (!cancelled) {
              setFolderCounts((current) => ({
                ...current,
                [folderId]: getFolderItemCount(data),
              }));
            }
          } catch {
            // Ignore folders that fail to load counts.
          }
        }
      );
    };

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, [folderIdsKey, mmCode, queryClient, folderIds]);

  useEffect(() => {
    if (!activeChildrenData) return;

    setFolderCounts((current) => ({
      ...current,
      [activeChildrenData.folder.id]: getFolderItemCount(activeChildrenData),
    }));
  }, [activeChildrenData]);

  useEffect(() => {
    if (mmCode === null) return;

    return queryClient.getQueryCache().subscribe(() => {
      syncCountFromCache();
    });
  }, [mmCode, queryClient, syncCountFromCache]);

  return { folderCounts };
}
